import React from 'react';

import { Point2D as Point2DInterface } from '@paragon/contract-library';
import { distanceLessThan, Line2D, LineSegment2D, Point2D, point2DEquals } from '@paragon/geometry-library';
import { findAndCalculateLine } from '@paragon/relative-geometry';
import { assertExhaustiveSwitch, notNullish } from '@paragon/utilities';

import { useAppSelector } from '../../../components/hooks';
import { createPreviewLinesForBasicInsertion, toCalculatedLines } from '../../../store/actions/basicInsertionActions';
import { getActiveComponentDesign } from '../../../store/selectors/getActiveComponentDesignSelector';
import {
  BasicInsertionSecondSelection,
  BasicLineDefinition,
  ReferenceLineIntersection,
} from '../../../store/slices/applicationSlice';
import { PointSlopeLineInsertionDimension } from '../../dimension/lines/PointSlopeLineInsertionDimension';
import { QuickDistanceDimension } from '../../dimension/QuickDistanceDimension';
import { useMousePositionWithBasicInsertionSnapping } from '../../hooks/useMousePositionWithBasicInsertionSnapping';
import { getCameraClickRadius } from '../../ThreeJsSceneActions';
import { Line } from '../../util/Line';

interface Props {
  readonly definition: BasicLineDefinition;
  readonly hoveredIntersection: ReferenceLineIntersection | null;
  readonly hoveredLineGuids: ReadonlySet<string>;
}

export function BasicInsertionPreviewVisuals({ definition, hoveredIntersection, hoveredLineGuids }: Props) {
  const mousePosition = useMousePositionWithBasicInsertionSnapping();

  const componentDesign = useAppSelector(getActiveComponentDesign);
  if (componentDesign == null) return null;

  // Mouse position can be null if no movement has occurred since mounting this component. Nothing we can do about that.
  if (!mousePosition) return null;

  const { firstSelection } = definition;
  if (firstSelection == null) return null;

  let secondSelection: BasicInsertionSecondSelection | undefined;

  switch (firstSelection.type) {
    case 'intersection': {
      // from a first intersection, the two possible second selections are:
      //   - another intersection (will become a point-to-point line)
      //   - a point in space (will become a point-slope line)

      if (hoveredIntersection) {
        secondSelection = {
          type: 'intersection',
          value: hoveredIntersection,
        };
      } else {
        const firstPoint = Point2D.fromObject(firstSelection.value.point);
        secondSelection = {
          type: 'direction',
          value: firstPoint.directionToPoint(mousePosition),
        };
      }

      break;
    }

    case 'line': {
      const baseLineGuid = firstSelection.value;

      if (hoveredIntersection?.point != null) {
        // line + intersection => perpendicular line
        secondSelection = {
          type: 'intersection',
          value: hoveredIntersection,
        };

        break;
      } else if (hoveredLineGuids.size > 0) {
        const firstSelectionLine = findAndCalculateLine(firstSelection.value, componentDesign.component);
        if (firstSelectionLine == null) return null;

        // line + same line => exact snapping; no preview visual needed
        const firstSelectionSnappedPosition = Point2D.fromObject(mousePosition).projectOntoLine(firstSelectionLine);
        const distanceFromFirstClick = firstSelectionSnappedPosition.distanceTo(mousePosition);
        if (distanceLessThan(distanceFromFirstClick, getCameraClickRadius())) return null;

        const hoveredTargetLine = Array.from(hoveredLineGuids)
          .map((lineGuid) => {
            const line = findAndCalculateLine(lineGuid, componentDesign.component);
            return line ? ([lineGuid, line] as const) : null;
          })
          .filter(notNullish)

          // division lines between non-parallel lines are supported for calculation because they can be imported from
          //   other sources, but they are not supported for insertion
          .filter(([_, line]) => line.isParallelTo(firstSelectionLine))[0];

        if (hoveredTargetLine == null) {
          // this branch is deliberately empty to fall through to a parallel line below, which is the desired behavior.
        } else {
          // line + different line => division line
          secondSelection = {
            type: 'line',
            value: hoveredTargetLine[0],
          };

          break;
        }
      }

      // otherwise, line + point in space => parallel line

      const baseLine = findAndCalculateLine(baseLineGuid, componentDesign.component)!;

      const angleLine = Line2D.fromPoints(baseLine.basePoint, mousePosition);
      const angle = baseLine.direction.angleTo(angleLine.direction);
      const sign = angle >= 0 ? 1 : -1;

      secondSelection = {
        type: 'offset',
        value: mousePosition.distanceToLine(baseLine) * sign,
      };

      break;
    }

    default:
      assertExhaustiveSwitch(firstSelection);
  }

  const previewLines = createPreviewLinesForBasicInsertion(definition, secondSelection);
  if (previewLines == null) return null;

  const previewLineGeometries = toCalculatedLines(previewLines, componentDesign.component);

  const dimensionSegments = [];

  if (firstSelection.type === 'line' && secondSelection.type !== 'intersection' && previewLineGeometries.length > 0) {
    const firstSelectionLine = findAndCalculateLine(firstSelection.value, componentDesign.component)!;
    const dimensionTargets = [firstSelectionLine, ...previewLineGeometries];

    if (secondSelection?.type === 'line') {
      dimensionTargets.push(findAndCalculateLine(secondSelection.value, componentDesign.component)!);
    }

    for (let i = 0; i < dimensionTargets.length - 1; i++) {
      const startPoint = mousePosition.projectOntoLine(dimensionTargets[i]);
      const endPoint = mousePosition.projectOntoLine(dimensionTargets[i + 1]);

      // abort previewing if start and end points are the same; the user has not yet moved their mouse
      if (point2DEquals(startPoint, endPoint)) return null;

      dimensionSegments.push(new LineSegment2D(startPoint, endPoint));
    }
  }

  return (
    <>
      {previewLineGeometries.map((line, i) => (
        <PreviewLine key={i} basePoint={line.basePoint} endPoint={line.getPointAlongLine(42)} />
      ))}

      {dimensionSegments.map((segment, i) => (
        <QuickDistanceDimension key={i} start={segment.basePoint} end={segment.endPoint} distance={segment.length} />
      ))}

      {firstSelection.type === 'intersection' && secondSelection?.type === 'direction' && (
        <PointSlopeLineInsertionDimension basePoint={firstSelection.value.point} mousePosition={mousePosition} />
      )}
    </>
  );
}

function PreviewLine(props: { basePoint: Point2DInterface; endPoint: Point2DInterface }) {
  return (
    <Line
      infinite
      visible
      depthTest={false}
      points={[toPointArray(props.basePoint), toPointArray(props.endPoint)]}
      color={0xff0000}
    />
  );
}

function toPointArray(point: Point2DInterface): [number, number, number] {
  return [point.x, point.y, 0];
}
